import { useMemo, useState } from 'preact/hooks';
import { AppLayout } from '../../components/app/AppLayout';
import { RequireOnboarded } from '../../components/app/RequireOnboarded';
import { useI18n } from '../../i18n';
import { useGameData } from '../../state/game';
import { MatchCard } from '../../components/play/MatchCard';
import { GroupsView } from '../../components/play/GroupsView';
import { BracketView } from '../../components/play/BracketView';
import type { BundleMatch, Phase } from '../../lib/wcTypes';

type Tab = 'matches' | 'groups' | 'bracket';

const PHASE_LABEL: Record<Phase, string> = {
  group: 'group',
  r32: 'r32',
  r16: 'r16',
  qf: 'qf',
  sf: 'sf',
  third: 'third',
  final: 'final',
};

export function Play() {
  return (
    <RequireOnboarded>
      <AppLayout>
        <PlayInner />
      </AppLayout>
    </RequireOnboarded>
  );
}

function PlayInner() {
  const { t } = useI18n();
  const { bundle, loading } = useGameData();
  const [tab, setTab] = useState<Tab>('matches');

  if (loading || !bundle) {
    return <div class="bc-spinner">{t('app.loading')}</div>;
  }

  return (
    <>
      <div class="bc-phasetabs">
        {(['matches', 'groups', 'bracket'] as Tab[]).map((tk) => (
          <button
            key={tk}
            class={`bc-pill${tab === tk ? ' is-active' : ''}`}
            onClick={() => setTab(tk)}
          >
            {t(`play.tabs.${tk}`)}
          </button>
        ))}
      </div>

      {tab === 'matches' && <MatchesPhase />}
      {tab === 'groups' && <GroupsView />}
      {tab === 'bracket' && <BracketView />}
    </>
  );
}

function MatchesPhase() {
  const { t } = useI18n();
  const { bundle } = useGameData();

  // Distinct matchdays in order, each with a derived label.
  const matchdays = useMemo(() => {
    if (!bundle) return [];
    const map = new Map<number, BundleMatch[]>();
    for (const m of bundle.matches) {
      const arr = map.get(m.matchday) ?? [];
      arr.push(m);
      map.set(m.matchday, arr);
    }
    return [...map.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([md, ms]) => {
        const phase = ms[0].phase;
        const label = phase === 'group' ? `${t('play.md')}${md}` : t(`play.bracket.${PHASE_LABEL[phase]}`);
        // Kickoff order, not match number — KO numbering doesn't follow
        // the calendar (e.g. R16 #92 kicks off after #93).
        return {
          md,
          label,
          matches: ms.sort(
            (x, y) => x.kickoff_utc.localeCompare(y.kickoff_utc) || x.number - y.number,
          ),
        };
      });
  }, [bundle, t]);

  // Default to the matchday holding the next non-finished match.
  const defaultMd = useMemo(() => {
    for (const d of matchdays) {
      if (d.matches.some((m) => m.status === 'upcoming' || m.status === 'live')) return d.md;
    }
    return matchdays.length ? matchdays[matchdays.length - 1].md : 1;
  }, [matchdays]);

  const [selected, setSelected] = useState<number | null>(null);
  const activeMd = selected ?? defaultMd;
  const current = matchdays.find((d) => d.md === activeMd) ?? matchdays[0];

  if (!current) return <p class="bc-empty">{t('play.empty')}</p>;

  return (
    <>
      <div class="bc-mdstrip">
        {matchdays.map((d) => (
          <button
            key={d.md}
            class={`bc-mdchip${d.md === activeMd ? ' is-active' : ''}`}
            onClick={() => setSelected(d.md)}
          >
            {d.label}
          </button>
        ))}
      </div>
      <div class="mc-list">
        {current.matches.map((m) => (
          <MatchCard key={m.number} match={m} />
        ))}
      </div>
    </>
  );
}
